import { Button } from '@/components/atoms';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import GoogleIcon from '@/assets/svg/google-icon.svg?react';
import { useLocation, useNavigate, useOutletContext } from 'react-router';
import type { OutletContext } from '@/components/layouts';
import {
  navigateAfterGoogleAuth,
  parseGoogleIdToken,
  resolveGoogleLoginRouting,
  toOAuthUser,
  toOAuthUserFromSetupToken,
} from '@/lib/auth/google-id-token';
import { setGoogleSetupToken } from '@/lib/auth/google-setup-token';
import { routes } from '@/lib/constants';
import { useLoginGoogle } from '@/api';

const PASSWORD_ACCOUNT_MESSAGE = /sign in with your email and password/i;

export const ContinueWithGoogle = () => {
  const { setCredentials, setOrigin, setAccessToken } =
    useOutletContext<OutletContext>();
  const navigate = useNavigate();
  const location = useLocation();

  const { mutate: loginWithGoogle, isPending } = useLoginGoogle(
    (data) => {
      const payload = data.data;
      if (!payload) return;

      const setupToken = payload.setupToken;
      if (setupToken) {
        // Keep for complete-signup body — not used as Authorization Bearer.
        setGoogleSetupToken(setupToken);
        setAccessToken(setupToken);
        setCredentials((current) => {
          if (current) return current;
          return toOAuthUserFromSetupToken(setupToken) ?? undefined;
        });
      }

      const routing = resolveGoogleLoginRouting(payload);
      navigateAfterGoogleAuth(navigate, {
        ...routing,
        search: location.search,
      });
    },
    (error) => {
      // Response C — email already registered with password.
      if (PASSWORD_ACCOUNT_MESSAGE.test(error.message)) {
        navigate(routes.login, { replace: true });
      }
    }
  );

  const handleCredentialSuccess = (response: CredentialResponse) => {
    const idToken = response.credential;
    if (!idToken) return;

    const parsed = parseGoogleIdToken(idToken);
    const user = parsed ? toOAuthUser(parsed) : null;
    if (user) setCredentials(user);
    setOrigin('google');

    loginWithGoogle({ token: idToken });
  };

  return (
    <div className="relative col-span-2">
      <Button
        type="button"
        isLoading={isPending}
        className="w-full pointer-events-none"
        variant="outline"
        size="lg"
        tabIndex={-1}
        aria-hidden
      >
        <GoogleIcon /> Continue with Google
      </Button>

      <div className="absolute inset-0 flex items-center justify-center overflow-hidden opacity-0">
        <GoogleLogin
          onSuccess={handleCredentialSuccess}
          onError={() => console.error('Google login failed')}
          useOneTap={false}
          width="400"
          text="continue_with"
        />
      </div>
    </div>
  );
};
